import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, DatePicker, Descriptions, Form, Grid, Input, InputNumber,
  Modal, Popover, Select, Space, Spin, Typography, message, theme } from "antd";
import { DownOutlined, FileExcelOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, SlidersOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import apiClient from "../../api/apiClient";
import API_CONFIG from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import { useTreasuryAccountSuggestion } from "../../hooks/useCommercialTreasurySuggestion";
import LogisticsPaymentList from "./LogisticsPaymentList";
import { LOGISTICS_PAYMENT_SOURCES, formatLogisticsAmount, logisticsPaymentPermission, logisticsPaymentReference } from "./logisticsPaymentConfig";
import "./LogisticsPayments.css";

const { Title, Text } = Typography;
const errorMessage = (error) => error.response?.data?.message || error.response?.data?.error || "La demande n'a pas pu être traitée.";
const canceled = (error) => error.code === "ERR_CANCELED" || error.name === "CanceledError";
const freshKey = () => window.crypto?.randomUUID?.() || `payment_${Date.now()}_${Math.random().toString(36).slice(2)}`;

export default function LogisticsPayments({ paymentType = "in" }) {
  const { hasPermission } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { token } = theme.useToken();
  const breakpoints = Grid.useBreakpoint();
  const mobile = !breakpoints.md;
  const [form] = Form.useForm();
  const [modalApi, modalHolder] = Modal.useModal();
  const [feedback, feedbackHolder] = message.useMessage();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ source: "all", search: "", dates: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [refresh, setRefresh] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [documents, setDocuments] = useState([]);
  const [chosenDocument, setChosenDocument] = useState(null);
  const [documentSearch, setDocumentSearch] = useState("");
  const [documentLoading, setDocumentLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [modes, setModes] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState("");
  const [entities, setEntities] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [entitiesError, setEntitiesError] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [exporting, setExporting] = useState(false);
  const exportRequest = useRef(null);
  const [busy, setBusy] = useState(false);
  const requestId = useRef(0);
  const detailId = useRef(0);
  const deleteDialog = useRef(null);
  const submitting = useRef(false);
  const idempotencyKey = useRef(null);
  const context = `${selectedCompany}:${selectedWarehouse}:${paymentType}`;
  const liveContext = useRef(context);
  liveContext.current = context;
  const exportContext = `${context}:${JSON.stringify(filters)}`;
  const liveExportContext = useRef(exportContext);
  liveExportContext.current = exportContext;
  const source = Form.useWatch("source", form) || dialog?.source;
  const documentId = Form.useWatch("document_id", form);
  const canManage = (origin, action) => hasPermission(logisticsPaymentPermission(origin, action));
  const viewSources = useMemo(() => Object.keys(LOGISTICS_PAYMENT_SOURCES).filter(
    (origin) => LOGISTICS_PAYMENT_SOURCES[origin].type === paymentType && hasPermission(logisticsPaymentPermission(origin, "view"))), [paymentType, hasPermission]);
  const createSources = Object.keys(LOGISTICS_PAYMENT_SOURCES).filter(
    (origin) => LOGISTICS_PAYMENT_SOURCES[origin].type === paymentType && canManage(origin, "create"));
  const allowed = viewSources.length > 0;
  const selectedDocument = dialog?.kind === "edit" ? dialog.row :
    (Number(chosenDocument?.id) === Number(documentId) ? chosenDocument : documents.find((document) => Number(document.id) === Number(documentId)));
  const maxAmount = selectedDocument ? Math.round((Number(selectedDocument.balance_due || 0) +
    (dialog?.kind === "edit" ? Number(dialog.row.amount) : 0)) * 100) / 100 : null;
  const endpoint = API_CONFIG.apiUrl("/logistics/payments");
  const compatibleAccounts = useMemo(() => accounts.filter((account) =>
    !selectedDocument?.currency_id || Number(account.currency_id) === Number(selectedDocument.currency_id)),
  [accounts, selectedDocument?.currency_id]);
  const { onModeChange: suggestAccount, onAccountChange: selectAccount } = useTreasuryAccountSuggestion(form, {
    companyId: selectedCompany, warehouseId: selectedWarehouse, moduleScope: "logistics",
    enabled: !!dialog && dialog.kind !== "view" && !!selectedDocument,
    referenceCurrencyOnly: source === "expense",
    modeField: "payment_mode_id", accountField: "treasury_account_id", accounts: compatibleAccounts,
  });
  const entityLabel = paymentType === "in" ? "Client" : "Fournisseur";
  const queryParams = () => ({ company_id: selectedCompany, warehouse_id: selectedWarehouse, payment_type: paymentType,
    source: filters.source, search: filters.search, entity_name: filters.entity,
    treasury_account_id: filters.account, payment_mode_id: filters.mode,
    start_date: filters.dates?.[0]?.format("YYYY-MM-DD"), end_date: filters.dates?.[1]?.format("YYYY-MM-DD") });

  useEffect(() => {
    deleteDialog.current?.destroy(); deleteDialog.current = null;
    setRows([]); setTotals([]); setTotal(0); setPage(1); setSearch("");
    setFilters({ source: "all", search: "", dates: null });
    setDialog(null); setDialogError(""); setDocuments([]); setChosenDocument(null); setAccounts([]); setModes([]);
    setEntities([]); setAdvancedOpen(false);
    detailId.current += 1;
  }, [context]);

  useEffect(() => {
    setSelectedKeys([]); exportRequest.current?.abort(); exportRequest.current = null; setExporting(false);
    return () => { exportRequest.current?.abort(); };
  }, [exportContext, refresh, hasPermission]);

  useEffect(() => {
    const controller = new AbortController();
    const current = ++requestId.current;
    setRows([]); setTotals([]); setTotal(0); setLoadError("");
    if (!allowed || !selectedCompany || !selectedWarehouse) { setLoading(false); return () => controller.abort(); }
    setLoading(true);
    apiClient.get(endpoint, { signal: controller.signal, params: {
      company_id: selectedCompany, warehouse_id: selectedWarehouse, payment_type: paymentType,
      page, limit: pageSize, source: filters.source, search: filters.search, entity_name: filters.entity,
      treasury_account_id: filters.account, payment_mode_id: filters.mode,
      start_date: filters.dates?.[0]?.format("YYYY-MM-DD"), end_date: filters.dates?.[1]?.format("YYYY-MM-DD"),
    } }).then(({ data }) => {
      if (current !== requestId.current || controller.signal.aborted) return;
      setRows(data.data || []); setTotals(data.totals || []); setTotal(Number(data.total || 0));
    }).catch((error) => {
      if (current === requestId.current && !controller.signal.aborted && !canceled(error)) setLoadError(errorMessage(error));
    }).finally(() => { if (current === requestId.current && !controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [allowed, endpoint, selectedCompany, selectedWarehouse, paymentType, page, pageSize, filters, refresh, hasPermission]);

  useEffect(() => {
    if (!allowed || !selectedCompany || !selectedWarehouse) return undefined;
    const controller = new AbortController();
    setAccounts([]); setModes([]); setOptionsLoading(true); setOptionsError("");
    apiClient.get(`${endpoint}/options`, { signal: controller.signal, params: {
      company_id: selectedCompany, warehouse_id: selectedWarehouse,
    } }).then(({ data }) => {
      if (controller.signal.aborted) return;
      setAccounts(data.accounts || []); setModes(data.modes || []);
    }).catch((error) => { if (!controller.signal.aborted && !canceled(error)) setOptionsError(errorMessage(error)); })
      .finally(() => { if (!controller.signal.aborted) setOptionsLoading(false); });
    return () => controller.abort();
  }, [allowed, endpoint, selectedCompany, selectedWarehouse, hasPermission, refresh]);

  useEffect(() => {
    const controller = new AbortController();
    setEntities([]); setEntitiesError("");
    if (!allowed || !selectedCompany || !selectedWarehouse) { setEntitiesLoading(false); return () => controller.abort(); }
    setEntitiesLoading(true);
    apiClient.get(`${endpoint}/entities`, { signal: controller.signal,
      params: { company_id: selectedCompany, warehouse_id: selectedWarehouse, payment_type: paymentType } })
      .then(({ data }) => { if (!controller.signal.aborted) setEntities(data.entities || []); })
      .catch((error) => { if (!controller.signal.aborted && !canceled(error)) setEntitiesError(errorMessage(error)); })
      .finally(() => { if (!controller.signal.aborted) setEntitiesLoading(false); });
    return () => controller.abort();
  }, [allowed, endpoint, selectedCompany, selectedWarehouse, paymentType, hasPermission, refresh]);

  useEffect(() => {
    if (dialog?.kind !== "create" || !source) return undefined;
    const controller = new AbortController();
    setDocuments([]); setDocumentLoading(true);
    const timeout = setTimeout(() => {
      apiClient.get(`${endpoint}/documents`, { signal: controller.signal,
        params: { company_id: selectedCompany, warehouse_id: selectedWarehouse, source, search: documentSearch } })
        .then(({ data }) => { if (!controller.signal.aborted) setDocuments(data.data || []); })
        .catch((error) => { if (!controller.signal.aborted && !canceled(error)) setDialogError(errorMessage(error)); })
        .finally(() => { if (!controller.signal.aborted) setDocumentLoading(false); });
    }, documentSearch ? 250 : 0);
    return () => { clearTimeout(timeout); controller.abort(); };
  }, [dialog?.kind, source, documentSearch, endpoint, selectedCompany, selectedWarehouse]);

  useEffect(() => {
    if (dialog && !canManage(dialog.source, dialog.kind === "view" ? "view" : dialog.kind)) { detailId.current += 1; setDialog(null); }
    // The selected origin can change inside the creation form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  useEffect(() => {
    if (dialog?.kind !== "create") return;
    form.resetFields(); form.setFieldsValue({ source: dialog.initialSource, payment_date: dayjs() });
  }, [dialog?.kind, dialog?.initialSource, form]);

  const changeFilter = (patch) => { setPage(1); setFilters((previous) => ({ ...previous, ...patch })); };
  const closeDialog = () => { if (!submitting.current) { detailId.current += 1; setDialog(null); setDialogError(""); setDialogLoading(false); } };
  const create = () => {
    const origin = createSources[0];
    if (!origin) return;
    idempotencyKey.current = freshKey(); setDialogLoading(false); setDialogError(""); setDocumentSearch(""); setChosenDocument(null);
    setDialog({ kind: "create", source: origin, initialSource: origin });
  };
  const openExisting = async (row, kind) => {
    if (!canManage(row.source, kind === "view" ? "view" : "edit")) return;
    const current = ++detailId.current;
    const capturedContext = context;
    setDialogError(""); setDialog({ kind, source: row.source, row }); setDialogLoading(true);
    try {
      const { data } = await apiClient.get(`${endpoint}/${row.source}/${row.id}`, {
        params: { company_id: selectedCompany, warehouse_id: selectedWarehouse },
      });
      if (current !== detailId.current || liveContext.current !== capturedContext) return;
      setDialog({ kind, source: row.source, row: data.data, ready: true });
      form.resetFields(); form.setFieldsValue({ ...data.data, source: row.source, amount: Number(data.data.amount),
        treasury_account_id: data.data.treasury_account_id ? Number(data.data.treasury_account_id) : undefined,
        payment_mode_id: data.data.payment_mode_id ? Number(data.data.payment_mode_id) : undefined,
        payment_date: dayjs(data.data.payment_date) });
    } catch (error) { if (current === detailId.current && liveContext.current === capturedContext) setDialogError(errorMessage(error)); }
    finally { if (current === detailId.current) setDialogLoading(false); }
  };

  const submit = async () => {
    if (submitting.current || !dialog) return;
    const capturedContext = context;
    const capturedDialog = detailId.current;
    let values;
    try { values = await form.validateFields(); } catch { return; }
    if (submitting.current || liveContext.current !== capturedContext || capturedDialog !== detailId.current) return;
    const action = dialog.kind === "create" ? "create" : "edit";
    if (!canManage(values.source, action)) return;
    submitting.current = true; setBusy(true); setDialogError("");
    const payload = { ...values, document_id: action === "edit" ? dialog.row.document_id : values.document_id,
      company_id: selectedCompany, warehouse_id: selectedWarehouse,
      payment_date: values.payment_date.format("YYYY-MM-DD") };
    try {
      if (action === "create") await apiClient.post(`${endpoint}/${values.source}`, payload, {
        headers: { "X-Idempotency-Key": idempotencyKey.current },
      });
      else await apiClient.put(`${endpoint}/${values.source}/${dialog.row.id}`, payload);
      if (liveContext.current !== capturedContext) return;
      feedback.success("Paiement enregistré."); setDialog(null); setRefresh((value) => value + 1);
    } catch (error) { if (liveContext.current === capturedContext) setDialogError(errorMessage(error)); }
    finally { submitting.current = false; setBusy(false); }
  };

  const remove = (row) => {
    if (!canManage(row.source, "delete") || submitting.current || deleteDialog.current) return;
    const capturedContext = context;
    const confirmation = modalApi.confirm({ title: "Supprimer ce paiement ?", content: `Le paiement de ${formatLogisticsAmount(row.amount, row.currency_symbol)} sera supprimé. Le solde du document et la trésorerie seront recalculés.`,
      okText: "Supprimer", cancelText: "Annuler", okButtonProps: { danger: true },
      afterClose: () => { if (deleteDialog.current === confirmation) deleteDialog.current = null; },
      onOk: async () => {
        if (liveContext.current !== capturedContext || submitting.current) return;
        submitting.current = true; setBusy(true);
        try {
          await apiClient.delete(`${endpoint}/${row.source}/${row.id}`, { params: { company_id: selectedCompany, warehouse_id: selectedWarehouse } });
          if (liveContext.current === capturedContext) { feedback.success("Paiement supprimé."); setRefresh((value) => value + 1); }
        } catch (error) { if (liveContext.current === capturedContext) feedback.error(errorMessage(error)); throw error; }
        finally { submitting.current = false; setBusy(false); }
      },
    });
    deleteDialog.current = confirmation;
  };

  const download = async () => {
    if (exportRequest.current || loading || !total || !allowed) return;
    const controller = new AbortController();
    const capturedContext = exportContext;
    exportRequest.current = controller; setExporting(true);
    try {
      const response = await apiClient.post(`${endpoint}/export`, selectedKeys.length ? { selection: selectedKeys } : {}, {
        params: { ...queryParams(), format: "xlsx" }, responseType: "blob", signal: controller.signal, timeout: 120000,
      });
      if (controller.signal.aborted || liveExportContext.current !== capturedContext) return;
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url; link.download = `paiements-${paymentType === "in" ? "entrants" : "sortants"}-logistique-${dayjs().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      feedback.success("Export Excel téléchargé.");
    } catch (error) {
      if (controller.signal.aborted || liveExportContext.current !== capturedContext || canceled(error)) return;
      let reason = errorMessage(error);
      if (error.response?.data instanceof Blob) {
        try { const data = JSON.parse(await error.response.data.text()); reason = data.message || data.error || reason; } catch { /* Keep the generic message for a non-JSON error. */ }
      }
      if (liveExportContext.current === capturedContext && !controller.signal.aborted) feedback.error(reason);
    } finally {
      if (exportRequest.current === controller) { exportRequest.current = null; setExporting(false); }
    }
  };

  const title = paymentType === "in" ? "Paiements Entrants" : "Paiements Sortants";
  const extraFilters = Number(filters.source !== "all") + Number(!!filters.account) + Number(!!filters.mode);
  return <section className="logistics-payments" style={{ color: token.colorText, background: token.colorBgLayout,
    "--logistics-active-page-color": token.colorText, "--logistics-panel-bg": token.colorBgContainer,
    "--logistics-border-color": token.colorBorderSecondary, "--logistics-text-secondary": token.colorTextSecondary,
    "--logistics-primary-color": token.colorPrimary, "--logistics-hover-bg": token.colorFillTertiary }}>
    {modalHolder}{feedbackHolder}
    <Text type="secondary" className="logistics-payments__breadcrumb">Logistique / Paiements</Text>
    <div className="logistics-payments__heading"><div><Title level={2}>{title}</Title>
      <Text type="secondary">{paymentType === "in" ? "Encaissements des factures clients." : "Règlements fournisseurs et Charges Admin."}</Text></div></div>
    {!allowed ? <Alert type="warning" showIcon message="Vous n'avez pas accès à ces paiements." /> :
      !selectedCompany || !selectedWarehouse ? <Alert type="info" showIcon message="Sélectionnez une entreprise et un établissement." /> : <>
        <div className="logistics-payments__toolbar">
          <div className="logistics-payments__field"><label htmlFor="logistics-payment-search">Rechercher</label><Input id="logistics-payment-search" prefix={<SearchOutlined />} value={search}
            onChange={(event) => { setSearch(event.target.value); if (!event.target.value) changeFilter({ search: "" }); }}
            onPressEnter={() => changeFilter({ search })} onBlur={() => { if (search !== filters.search) changeFilter({ search }); }}
            placeholder="N° ou référence du paiement" allowClear /></div>
          <div className="logistics-payments__field"><label htmlFor="logistics-payment-entity">{entityLabel}</label><Select id="logistics-payment-entity" showSearch allowClear optionFilterProp="label" value={filters.entity}
            loading={entitiesLoading} placeholder={paymentType === "in" ? "Tous les clients" : "Tous les fournisseurs"}
            options={entities.map((name) => ({ value: name, label: name }))} onChange={(value) => changeFilter({ entity: value })} /></div>
          <div className="logistics-payments__field"><label htmlFor="logistics-payment-period">Période</label><DatePicker.RangePicker id={["logistics-payment-period", "logistics-payment-period-end"]}
            value={filters.dates} format="DD/MM/YYYY" placeholder={["Début", "Fin"]} onChange={(value) => changeFilter({ dates: value })} /></div>
          <div className="logistics-payments__toolbar-actions"><Popover placement="bottomRight" trigger="click" open={advancedOpen} onOpenChange={setAdvancedOpen}
            title="Filtres complémentaires" content={<div className="logistics-payments__advanced">
              {paymentType === "out" && <div className="logistics-payments__field"><label htmlFor="logistics-payment-origin">Origine</label><Select id="logistics-payment-origin" value={filters.source} onChange={(value) => changeFilter({ source: value })}
                options={[{ value: "all", label: "Toutes les origines autorisées" }, ...viewSources.map((origin) => ({ value: origin, label: LOGISTICS_PAYMENT_SOURCES[origin].label }))]} /></div>}
              <div className="logistics-payments__field"><label htmlFor="logistics-payment-account">Compte de trésorerie</label><Select id="logistics-payment-account" allowClear value={filters.account} loading={optionsLoading} placeholder="Tous les comptes"
                options={accounts.map((account) => ({ value: Number(account.id), label: account.name }))} onChange={(value) => changeFilter({ account: value })} /></div>
              <div className="logistics-payments__field"><label htmlFor="logistics-payment-mode">Mode de paiement</label><Select id="logistics-payment-mode" allowClear value={filters.mode} loading={optionsLoading} placeholder="Tous les modes"
                options={modes.map((mode) => ({ value: Number(mode.id), label: mode.name }))} onChange={(value) => changeFilter({ mode: value })} /></div>
              <Button onClick={() => changeFilter({ source: "all", account: undefined, mode: undefined })}>Réinitialiser ces filtres</Button>
            </div>}><Button className="logistics-payments__filter-toggle" icon={<SlidersOutlined />} aria-expanded={advancedOpen}>Filtres{extraFilters ? ` (${extraFilters})` : ""}<DownOutlined /></Button></Popover>
            <Button className="logistics-payments__export" icon={<FileExcelOutlined />} onClick={download} loading={exporting} disabled={loading || !total || busy}
              aria-label={selectedKeys.length ? `Exporter la sélection (${selectedKeys.length})` : "Exporter sur Excel"}>
              {selectedKeys.length ? `Exporter la sélection (${selectedKeys.length})` : "Exporter sur Excel"}</Button>
            {createSources.length > 0 && <Button className="logistics-payments__create" type="primary" icon={<PlusOutlined />} onClick={create} disabled={busy || exporting}>Nouveau paiement</Button>}
            <Button className="logistics-payments__refresh" icon={<ReloadOutlined />} aria-label="Actualiser" title="Actualiser" onClick={() => setRefresh((value) => value + 1)} disabled={loading || exporting} />
          </div>
        </div>
        {loadError && <Alert type="error" showIcon message={loadError} action={<Button onClick={() => setRefresh((value) => value + 1)}>Réessayer</Button>} />}
        {optionsError && <Alert type="warning" showIcon message={optionsError} action={<Button onClick={() => setRefresh((value) => value + 1)}>Recharger les options</Button>} />}
        {entitiesError && <Alert type="warning" showIcon message={entitiesError} action={<Button onClick={() => setRefresh((value) => value + 1)}>Recharger les tiers</Button>} />}
        <div className="logistics-payments__summary"><Space wrap><Text strong>{loading ? "Chargement des paiements…" : `${total} paiement${total > 1 ? "s" : ""}`}</Text>
          {!!selectedKeys.length && <><Text type="secondary">{selectedKeys.length} sélectionné{selectedKeys.length > 1 ? "s" : ""}</Text><Button type="link" disabled={exporting} onClick={() => setSelectedKeys([])}>Effacer la sélection</Button></>}</Space>
          <Space wrap className="logistics-payments__totals">{totals.map((item) => <Text strong key={item.currency_id || "unknown"}>Total · {formatLogisticsAmount(item.amount, item.currency_symbol)}</Text>)}</Space>
        </div>
        <LogisticsPaymentList rows={rows} loading={loading} mobile={mobile} busy={busy || exporting} entityLabel={entityLabel}
          selectedKeys={selectedKeys} onSelectionChange={setSelectedKeys}
          pagination={{ current: page, pageSize, total }} onPageChange={(nextPage, nextSize) => { setPage(nextSize !== pageSize ? 1 : nextPage); setPageSize(nextSize); }}
          onView={(row) => openExisting(row, "view")} onEdit={(row) => openExisting(row, "edit")} onDelete={remove} canManage={canManage} />
      </>}
    <Modal open={!!dialog} title={dialog?.kind === "view" ? "Détail du paiement" : dialog?.kind === "edit" ? "Modifier le paiement" : "Enregistrer un paiement"}
      onCancel={closeDialog} destroyOnClose width={680} maskClosable={!busy} closable={!busy}
      footer={dialog?.kind === "view" ? <Button onClick={closeDialog}>Fermer</Button> : <Space>
        <Button onClick={closeDialog} disabled={busy}>Annuler</Button><Button type="primary" onClick={submit} loading={busy} disabled={dialogLoading || (dialog?.kind === "edit" && !dialog.ready) || optionsLoading || !!optionsError || !selectedDocument}>Enregistrer</Button>
      </Space>}>
      <Spin spinning={dialogLoading || (dialog?.kind !== "view" && optionsLoading)}>
        {optionsError && dialog?.kind !== "view" && <Alert type="error" showIcon message={optionsError} style={{ marginBottom: 16 }} />}
        {dialogError && <Alert type="error" showIcon message={dialogError} style={{ marginBottom: 16 }} />}
        {dialog?.kind === "view" ? <Descriptions column={1} bordered size="small" items={[
          { key: "reference", label: "N° / Référence", children: logisticsPaymentReference(dialog.row) },
          { key: "source", label: "Origine", children: LOGISTICS_PAYMENT_SOURCES[dialog.source]?.label },
          { key: "entity", label: entityLabel, children: dialog.row.entity_name },
          { key: "warehouse", label: "Établissement", children: dialog.row.warehouse_name || "Non renseigné" },
          { key: "document", label: "Document", children: dialog.row.document_number || "Sans référence" },
          { key: "shipment", label: "Dossier", children: dialog.row.shipment_number || "Sans dossier" },
          { key: "date", label: "Date", children: dayjs(dialog.row.payment_date).format("DD/MM/YYYY") },
          { key: "amount", label: "Montant", children: formatLogisticsAmount(dialog.row.amount, dialog.row.currency_symbol) },
          { key: "account", label: "Compte", children: dialog.row.treasury_account_name || "Non renseigné" },
          { key: "mode", label: "Mode", children: dialog.row.payment_mode_name || "Non renseigné" },
          { key: "notes", label: "Notes", children: dialog.row.notes || "Aucune" },
        ]} /> : <Form form={form} layout="vertical" onValuesChange={() => setDialogError("")}>
          <Form.Item label="Origine" name="source" rules={[{ required: true }]}><Select disabled={dialog?.kind === "edit"}
            options={(dialog?.kind === "edit" ? [dialog.source] : createSources).map((origin) => ({ value: origin, label: LOGISTICS_PAYMENT_SOURCES[origin].label }))}
            onChange={(value) => { setDialog((previous) => ({ ...previous, source: value })); setDocumentSearch(""); setChosenDocument(null); form.setFieldsValue({ document_id: undefined, amount: undefined }); }} /></Form.Item>
          {dialog?.kind === "create" ? <Form.Item label="Document à régler" name="document_id" rules={[{ required: true, message: "Sélectionnez un document." }]}>
            <Select showSearch filterOption={false} onSearch={setDocumentSearch} loading={documentLoading} placeholder="Rechercher une facture ou une charge impayée"
              options={documents.map((document) => ({ value: document.id, label: `${document.document_number || "Sans référence"} · ${document.entity_name} · Solde ${formatLogisticsAmount(document.balance_due)}` }))}
              onChange={(value) => {
                const chosen = documents.find((document) => Number(document.id) === Number(value));
                setChosenDocument(chosen || null);
                const account = accounts.find((item) => Number(item.id) === Number(form.getFieldValue("treasury_account_id")));
                form.setFieldsValue({ amount: Number(chosen?.balance_due || 0),
                  ...(!account || Number(account.currency_id) !== Number(chosen?.currency_id) ? { treasury_account_id: undefined } : {}) });
              }} />
          </Form.Item> : <p><Text strong>{dialog?.row?.document_number}</Text><Text type="secondary"> · {dialog?.row?.entity_name}</Text></p>}
          {selectedDocument && <Alert type="info" showIcon message={`Montant disponible pour ce règlement : ${formatLogisticsAmount(maxAmount, selectedDocument.currency_symbol)}`} style={{ marginBottom: 16 }} />}
          <div className="logistics-payments__form-grid">
            <Form.Item label="Montant" name="amount" rules={[{ required: true, message: "Saisissez le montant." }, { validator: (_, value) => Number(value) > 0 && (maxAmount == null || Number(value) <= maxAmount) ? Promise.resolve() : Promise.reject(new Error("Le montant doit être positif et respecter le solde disponible.")) }]}><InputNumber min={0.01} precision={2} style={{ width: "100%" }} /></Form.Item>
            <Form.Item label="Date du paiement" name="payment_date" rules={[{ required: true }]}><DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} /></Form.Item>
            <Form.Item label="Compte de trésorerie" name="treasury_account_id" rules={[{ required: true, message: "Choisissez un compte." }]}><Select placeholder="Compte logistique" onChange={selectAccount} options={compatibleAccounts.map((account) => ({ value: Number(account.id), label: account.name }))} /></Form.Item>
            <Form.Item label="Mode de paiement" name="payment_mode_id" rules={[{ required: source === "expense", message: "Choisissez un mode de paiement." }]}><Select allowClear onChange={suggestAccount} options={modes.map((mode) => ({ value: Number(mode.id), label: mode.name }))} /></Form.Item>
          </div>
          {source !== "expense" && <Form.Item label="Référence du paiement" name="payment_reference"><Input maxLength={100} /></Form.Item>}
          <Form.Item label="Notes" name="notes"><Input.TextArea rows={3} maxLength={1000} showCount /></Form.Item>
          {!optionsLoading && !accounts.length && <Alert type="warning" showIcon message="Aucun compte logistique actif n'est disponible pour cet établissement." />}
        </Form>}
      </Spin>
    </Modal>
  </section>;
}
